// Unified signals board for one persistent-agent thread. Every reason the
// thread can be woken — trigger doorbells, the drive (scorecard), timers,
// awaits — is one signal row in the host's registry. This panel shows them
// all in one overview (kind, state, fire stats, pause/resume/delete) with a
// per-signal fire log ("why did my agent run at 3am"), then embeds the
// trigger wizard (WatchesPanel) and the drive board (TrialScorecardPanel)
// below it.
//
// Mutations: trigger rows go through the signal-backed watch routes
// (PATCH/DELETE watches/:name). Drive rows are managed on the drive board
// below; timer/await rows are read-only here (armed via heartbeat routes or
// the agent's own signal_* / wake_when tools).
import { useClient } from '@cortex/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';

import { formatRelative } from '../../lib/format';

import { TrialScorecardPanel } from './TrialScorecardPanel';
import { WatchesPanel } from './WatchesPanel';

/** Raw PersistentAgentSignal row as served by GET …/signals. */
interface ISignalRow {
  id: string;
  name: string;
  kind: 'trigger' | 'drive' | 'timer' | 'await';
  tool: string | null;
  intervalSec: number;
  cron: string | null;
  state: 'active' | 'paused' | 'converged' | 'tripped';
  lastFiredAt: string | null;
  lastCheckedAt: string | null;
  fireCount: number;
  consecutiveErrors: number;
  maxFiresPerHour: number;
}

interface ISignalFire {
  id: string;
  firedAt: string;
  note: string | null;
}

const kindLabel: Record<ISignalRow['kind'], string> = {
  trigger: 'trigger',
  drive: 'drive',
  timer: 'timer',
  await: 'await',
};

const stateColor: Record<ISignalRow['state'], string> = {
  active: 'var(--status-ok, #2f9e44)',
  paused: 'var(--t-muted, #868e96)',
  converged: 'var(--interface-iris, #5c7cfa)',
  tripped: 'var(--status-danger, #e03131)',
};

function SignalFires({ base, name }: { base: string; name: string }) {
  const client = useClient();
  const fires = useQuery({
    queryKey: ['pa-signal-fires', base, name],
    queryFn: () => client.http.get(`${base}/signals/${name}/fires`).json<ISignalFire[]>(),
  });
  if (fires.isLoading) return <p className="t-xs t-muted">Loading fires…</p>;
  if (!fires.data?.length) return <p className="t-xs t-muted">No fires recorded yet.</p>;
  return (
    <ul style={{ margin: '4px 0 0', paddingLeft: 16, listStyle: 'disc' }}>
      {fires.data.map((f) => (
        <li key={f.id} className="t-xs t-muted">
          {formatRelative(f.firedAt)}
          {f.note ? ` — ${f.note}` : ''}
        </li>
      ))}
    </ul>
  );
}

export function SignalsPanel({
  agentId,
  threadId,
  status,
}: {
  agentId: string;
  threadId: string;
  status: string | null;
}) {
  const client = useClient();
  const qc = useQueryClient();
  const base = `v1/persistent-agent-groups/${agentId}/agents/${threadId}`;
  const [expanded, setExpanded] = useState<string | null>(null);

  const signals = useQuery({
    queryKey: ['pa-signals', threadId],
    queryFn: () => client.http.get(`${base}/signals`).json<ISignalRow[]>(),
    refetchInterval: 10_000,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['pa-signals', threadId] }).catch(() => undefined);
    qc.invalidateQueries({ queryKey: ['persistent-agent-watches', threadId] }).catch(
      () => undefined,
    );
  };

  const patch = useMutation({
    mutationFn: ({ name, state }: { name: string; state: 'active' | 'paused' }) =>
      client.http.patch(`${base}/watches/${name}`, { json: { status: state } }).json(),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (name: string) => client.http.delete(`${base}/watches/${name}`),
    onSuccess: refresh,
  });

  const rows = signals.data ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <section style={{ padding: 'var(--sp-3, 12px)' }}>
        <h3 className="t-sm" style={{ margin: '0 0 8px' }}>
          Signals
        </h3>
        <p className="t-xs t-muted" style={{ margin: '0 0 12px' }}>
          Everything that can wake this thread. Trigger doorbells and the drive are managed below;
          timers and awaits are armed by the agent (or the heartbeat UI for timers).
        </p>
        {signals.isLoading ? (
          <p className="t-xs t-muted">Loading signals…</p>
        ) : rows.length === 0 ? (
          <p className="t-xs t-muted">No signals — the thread only wakes when you talk to it.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr className="t-xs t-muted" style={{ textAlign: 'left' }}>
                <th style={{ padding: '4px 8px 4px 0' }}>name</th>
                <th style={{ padding: '4px 8px' }}>kind</th>
                <th style={{ padding: '4px 8px' }}>source</th>
                <th style={{ padding: '4px 8px' }}>state</th>
                <th style={{ padding: '4px 8px' }}>last fired</th>
                <th style={{ padding: '4px 8px' }}>fires</th>
                <th style={{ padding: '4px 8px' }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <Fragment key={s.id}>
                  <tr className="t-sm" style={{ borderTop: '1px solid var(--line, #dee2e6)' }}>
                    <td
                      style={{
                        padding: '6px 8px 6px 0',
                        fontFamily: 'var(--font-mono, monospace)',
                      }}
                    >
                      {s.name}
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      <span className="t-xs">{kindLabel[s.kind]}</span>
                    </td>
                    <td className="t-xs t-muted" style={{ padding: '6px 8px' }}>
                      {s.kind === 'timer' ? (s.cron ?? 'one-shot') : (s.tool ?? '—')}
                      {s.kind !== 'timer' ? ` · ${s.intervalSec}s` : ''}
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      <span className="t-xs" style={{ color: stateColor[s.state] }}>
                        {s.state}
                        {s.consecutiveErrors > 0 ? ` (${s.consecutiveErrors} err)` : ''}
                      </span>
                    </td>
                    <td className="t-xs t-muted" style={{ padding: '6px 8px' }}>
                      {s.lastFiredAt ? formatRelative(s.lastFiredAt) : 'never'}
                    </td>
                    <td className="t-xs t-muted" style={{ padding: '6px 8px' }}>
                      {s.fireCount}/h≤{s.maxFiresPerHour}
                    </td>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                      <button
                        className="btn btn--sm"
                        onClick={() => setExpanded((v) => (v === s.id ? null : s.id))}
                        aria-expanded={expanded === s.id}
                        title="Show fire log"
                      >
                        fires
                      </button>{' '}
                      {s.kind === 'trigger' && s.state !== 'converged' && s.state !== 'tripped' && (
                        <button
                          className="btn btn--sm"
                          disabled={patch.isPending}
                          onClick={() =>
                            patch.mutate({
                              name: s.name,
                              state: s.state === 'active' ? 'paused' : 'active',
                            })
                          }
                        >
                          {s.state === 'active' ? 'pause' : 'resume'}
                        </button>
                      )}{' '}
                      {s.kind === 'trigger' && (
                        <button
                          className="btn btn--sm btn--danger"
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(s.name)}
                        >
                          delete
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded === s.id && (
                    <tr>
                      <td colSpan={7} style={{ padding: '0 8px 8px' }}>
                        <SignalFires base={base} name={s.name} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
        {(patch.isError || remove.isError) && (
          <div className="alert alert--danger" style={{ marginTop: 8 }}>
            {String(((patch.error ?? remove.error) as Error)?.message ?? 'Signal action failed')}
          </div>
        )}
      </section>
      <WatchesPanel agentId={agentId} threadId={threadId} status={status} />
      <TrialScorecardPanel agentId={agentId} threadId={threadId} />
    </div>
  );
}
